/**
 * EventThread — render event-discussion replies under a Live event card, over
 * Agape.ChatApi (SignalR hub). CORE-618: the GetStream path was removed; this is
 * now ChatApi-only (matches prod, where `event_thread_via_chat_api` was on).
 *
 * Two consumers:
 *   1. LiveDetailScreen.tsx — pass `liveId` + `masterProfileId` (+ optional
 *      `masterName`). Channel key is `master-circle-{masterProfileId}` and the
 *      parent event card is found by scanning the channel's messages for
 *      `custom.eventId === liveId`.
 *   2. EventThreadScreen.tsx (native) — pass `channelId` + `parentMessageId`
 *      directly (the card is matched by its `externalStreamId`).
 *
 * Auth/membership states:
 *   - Authed        → composer enabled; the server (CanPost) gates writes.
 *   - Not a member  → «Присоединиться» modal → `subscribeToMaster`, draft kept.
 *   - Anonymous     → locked input "Войди, чтобы присоединиться", tap → Login.
 *
 * If the channel can't be loaded we render a retriable error; if no event card
 * references this `liveId` we render the empty-state.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Typography } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { useChatContacts } from '../../context/ChatContactsContext';
import { useTheme } from '../../context/ThemeContext';
import { api } from '../../services/api';
import { resolveName } from '../../utils/displayName';
import { RootStackParamList } from '../../navigation/types';
import { JoinEventModal } from './JoinEventModal';
import { ChatApiChannel } from '../../services/chat/chatApiChannel';
import { classifyChannelError } from '../../services/chat/chatChannelError';
import type { ThreadMessage as ChatApiThreadMessage } from '../../services/chat/chatTypes';
import { friendlyApiError } from '../../services/errorMessages';
import { LevelBadge } from '../gamification/LevelBadge';
import { useLevelBadgesState } from '../gamification/useLevelBadges';
import type { LevelBadgeLoadStatus } from '../gamification/levelBadgeContract';
import type { LevelBadge as LevelBadgeData } from '../../types/auth';
import { fontWeights } from '../../styles/typographyTokens';
import { EmojiStrip } from './EmojiStrip';
import { EmojiText } from './EmojiText';
import { EmojiOnlyMessage } from './EmojiOnlyMessage';
import { EMOJI_ONLY_MAX, emojiOnlyTokens } from '../../utils/emojiOnlyMessage';
import { ReactionGlyph } from './ReactionGlyph';
import { insertEmojiToken } from '../../utils/emojiTokens';
import { brandifyEmoji } from '../../constants/emojiAliases';
import { groupByCanonicalReaction, ownReactionSlugs } from '../../constants/brandReactions';
import { useRewardReactions } from '../../hooks/useRewardReactions';
import { myReactionCalls } from '../../services/chat/myReactionCalls';

type EventThreadProps =
  | {
      liveId: number;
      masterProfileId: number;
      masterName?: string | null;
      // CORE-352 — opt-in: when no discussion card exists yet, render the
      // discussion shell (composer / join gate) so a member can start it,
      // instead of rendering nothing. Set on surfaces without their own
      // «be the first» CTA (the practice page).
      allowStart?: boolean;
      channelId?: undefined;
      parentMessageId?: undefined;
    }
  | {
      liveId?: undefined;
      masterProfileId?: number;
      masterName?: string | null;
      allowStart?: boolean;
      channelId: string;
      parentMessageId: string;
    };

/**
 * CORE-1685 (QA 19.10) — сколько страниц истории листать назад в поисках
 * карточки практики. Канал отдаёт новейшие 50 сообщений всего сообщества, а
 * корень обсуждения практики, загруженной давно, стоит много страниц глубже
 * (у мастера с двумя сотнями обсуждений — третья страница и дальше). Без
 * поиска композер выглядел рабочим, а отправка молча ничего не делала.
 * Предел тот же, что у обсуждений на дашборде (DiscussionThreadPanel).
 */
const MAX_ROOT_SEARCH_PAGES = 30;
const NO_ROOT_HINT = 'Обсуждение пока недоступно. Обновите страницу позже.';
const NO_ROOT_SEND_ERROR = 'Комментарий не отправлен: обсуждение пока недоступно. Обновите страницу позже.';

type ThreadMessage = {
  id: string;
  text?: string | null;
  user?: { id: string; name?: string | null; image?: string | null } | null;
  created_at?: string | Date | null;
  type?: string;
  frozen?: boolean;
  eventKind?: string;
  eventId?: string | number;
  status?: string;
  deeplink?: string;
  parent_id?: string | null;
  pinned?: boolean;
  /** CORE-1685 (QA 31.10) — the comment's reactions as the server counts them, and this client's own slugs. */
  reactions: { type: string; count: number }[];
  myReactions: string[];
};

export const EventThread: React.FC<EventThreadProps> = (props) => {
  const { isAuthenticated, user } = useAuth();
  const { refreshContacts } = useChatContacts();
  const { isDark } = useTheme();
  const myAppId = (user?.id || '').replace(/^user_/, '');
  const chatApiRef = useRef<ChatApiChannel | null>(null);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const liveIdMode = typeof props.liveId === 'number';
  const channelId = liveIdMode
    ? `master-circle-${props.masterProfileId}`
    : (props.channelId as string);
  const masterProfileId = liveIdMode ? props.masterProfileId : props.masterProfileId ?? 0;
  const masterName = props.masterName ?? null;
  const allowStart = props.allowStart === true;

  const [parent, setParent] = useState<ThreadMessage | null>(null);
  const [parentIsTopic, setParentIsTopic] = useState(false);
  const [parentMessageId, setParentMessageId] = useState<string | null>(
    liveIdMode ? null : (props.parentMessageId as string),
  );
  const [replies, setReplies] = useState<ThreadMessage[]>([]);
  const [isMember, setIsMember] = useState(false);
  // Bumped on join-success to reload the thread.
  const [reloadTick, setReloadTick] = useState(0);
  const [loading, setLoading] = useState(true);
  // CORE-1685 — корень ищется по старым страницам: пока идёт поиск — спиннер,
  // не нашли — честная подсказка вместо композера, который ничего не отправит.
  const [rootSearch, setRootSearch] = useState<'idle' | 'searching' | 'missing'>('idle');
  const cardIdRef = useRef<string | null>(null);
  const cardKeyRef = useRef<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // CORE-543 — keep a transient ChatApi failure (timeout / network blip) distinct
  // from a genuinely-gone discussion, so it renders a retriable error instead of
  // the permanent "Обсуждение недоступно." dead-end.
  const [loadError, setLoadError] = useState(false);
  // CORE-1127 — WatchChannel refuses a non-member with HubException("forbidden").
  // That's deterministic, not a network blip: render the join gate, not a retry.
  const [accessDenied, setAccessDenied] = useState(false);
  // CORE-1127 (att3) — the access token was rejected (HTTP 401 on negotiate, or
  // HubException("unauthenticated")). Authorization failure, not a connection
  // one: prompt a re-auth instead of «Проверьте соединение.» + «Повторить».
  const [authExpired, setAuthExpired] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const inputRef = useRef<TextInput | null>(null);
  // CORE-1685 (QA 17.10) — палитра комплекта у поля комментария. Композер
  // здесь свой, не из чата, и палитры в нём не было — а «комментарии к
  // практикам» в области задачи названы прямо. Ряд остаётся открытым, пока его
  // не закроют той же кнопкой: несколько картинок подряд — обычное дело.
  const [emojiOpen, setEmojiOpen] = useState(false);
  const pickEmoji = useCallback((type: string) => {
    setDraft((prev) => insertEmojiToken(prev, type));
    inputRef.current?.focus();
  }, []);
  // CORE-1790 — эмодзи с клавиатуры уходят токенами комплекта; закрытая
  // наградная остаётся Unicode.
  const { access: reactionAccess } = useRewardReactions();
  const canUseReaction = useCallback((type: string) => reactionAccess(type) === 'open', [reactionAccess]);
  // CORE-1685 (QA 23.10) — одна попытка вступить в канал на подписку/канал.
  const circleJoinTriedRef = useRef(false);

  // Anonymous: skip the hub entirely — render gate only.
  const anonymous = !isAuthenticated;

  // Load the channel over Agape.ChatApi. Finds the event-card PARENT among the
  // channel's messages (by custom.eventId in liveId mode, or by the card's
  // externalStreamId == the passed id in direct mode). Replies are messages
  // whose parent (quoted) is that card.
  //
  // CORE-1127 — WatchChannel gates READS too, not just writes: it throws
  // HubException("forbidden") for a non-member and HubException("channel not
  // found") for a missing channel. classifyChannelError turns those into the
  // join gate / empty-state branches; only a genuine network failure stays the
  // retriable «Проверьте соединение.» error.
  useEffect(() => {
    if (anonymous || !myAppId) return;
    let disposed = false;
    setLoading(true);
    setUnavailable(false);
    setLoadError(false);
    setAccessDenied(false);
    setAuthExpired(false);
    const src = new ChatApiChannel(channelId, myAppId);
    chatApiRef.current = src;
    const cardKey = `${channelId}:${liveIdMode ? props.liveId : props.parentMessageId}`;
    if (cardKeyRef.current !== cardKey) {
      cardKeyRef.current = cardKey;
      cardIdRef.current = null;
      circleJoinTriedRef.current = false;
    }

    const findCard = (all: ChatApiThreadMessage[]) => all.find((m) => {
      const custom = (m.raw as any)?.custom || {};
      if (liveIdMode) {
        const eid = custom.eventId ?? custom.event_id;
        return eid != null && Number(eid) === (props.liveId as number);
      }
      const ext = (m.raw as any)?.externalStreamId ?? (m.raw as any)?.ExternalStreamId;
      return ext != null && String(ext) === String(props.parentMessageId);
    });

    // CORE-1685 (QA 19.10) — карточки нет среди новейших 50: по известному id
    // тред тянется одним запросом (после переподключения канал перечитывает
    // те же 50 и карточка выпадает снова), иначе история листается назад
    // страницами, пока карточка не найдётся. Поиск ограничен и не
    // повторяется: исчерпали страницы — обсуждения у практики нет.
    let searching = false;
    let gaveUp = false;
    const ensureCard = async () => {
      if (searching || disposed) return;
      if (gaveUp && !cardIdRef.current) return;
      searching = true;
      setRootSearch('searching');
      try {
        const known = cardIdRef.current;
        if (known) {
          // CORE-620 (возврат QA 30.10) — сбой треда теперь исключение; как и
          // раньше, он не мешает листать историю ниже.
          await src.loadThread(known).catch(() => false);
          if (disposed) return;
          if (findCard(src.getMessages())) { setRootSearch('idle'); return; }
        }
        for (let i = 0; !disposed && i < MAX_ROOT_SEARCH_PAGES; i++) {
          const got = await src.loadOlder();
          if (findCard(src.getMessages())) { setRootSearch('idle'); return; }
          if (!got) break;
        }
        gaveUp = true;
        if (!disposed) setRootSearch('missing');
      } catch {
        gaveUp = true;
        if (!disposed) setRootSearch('missing');
      } finally {
        searching = false;
      }
    };

    const sync = () => {
      if (disposed) return;
      const all = src.getMessages();
      const card = findCard(all);
      if (card) { cardIdRef.current = card.id; setRootSearch('idle'); }
      const pid = card?.id ?? null;
      // CORE-1655 — у темы корень обсуждения служебный: название и описание уже
      // стоят в шапке обсуждения, а пустой пузырь «от сообщества» над репликами
      // читался бы как сломанное сообщение.
      setParentIsTopic(((card?.raw as any)?.custom?.eventKind ?? null) === 'topic');
      setParentMessageId(pid);
      setParent(card ? chatApiToLocal(card) : null);
      setReplies(pid ? all.filter((m) => m.quoted?.id === pid).map(chatApiToLocal) : []);
      setIsMember(true); // authed; server gates writes
      setLoading(false);
      if (!card) void ensureCard();
    };

    src.onChange(sync);
    src.connect().then(sync).catch((err: unknown) => {
      if (disposed) return;
      // Deterministic access refusals (CORE-1127) render their own branch; the
      // retriable connection error is reserved for genuine transient failures.
      const refusal = classifyChannelError(err);
      // CORE-1685 (QA 23.10) — подписчик мастера, которого нет среди участников
      // канала `master-circle-N` (подписка старше канала: на dev QA Тест
      // подписан на мастера 4, REST отдаёт canPost, а WatchChannel отвечает
      // forbidden), видел «подпишись на мастера» и не мог написать.
      // `POST master-circles/{id}/join` идемпотентен и подписчика в канал
      // добавляет, а неподписанному отвечает 403 `subscription_required` —
      // поэтому одна попытка, спиннер не снимаем; не вышло — прежние ворота.
      if (refusal === 'forbidden' && masterProfileId > 0 && !circleJoinTriedRef.current) {
        circleJoinTriedRef.current = true;
        api.joinMasterCircle(masterProfileId)
          .then((r) => {
            if (disposed) return;
            if (r?.joined) { setReloadTick((n) => n + 1); return; }
            setLoading(false);
            setIsMember(false);
            setAccessDenied(true);
          })
          .catch(() => {
            if (disposed) return;
            setLoading(false);
            setIsMember(false);
            setAccessDenied(true);
          });
        return;
      }
      setLoading(false);
      switch (refusal) {
        case 'forbidden':
          // Non-member: show the «Присоединиться» gate. isMember stays false, and
          // accessDenied forces the discussion shell even without a parent card.
          setIsMember(false);
          setAccessDenied(true);
          break;
        case 'notFound':
          // No channel yet: fall through to the empty/shell state (rendered when
          // this surface has `allowStart`). isMember stays false.
          setIsMember(false);
          break;
        case 'unauthorized':
          // Token rejected (att3): re-auth branch, not the connection error.
          setIsMember(false);
          setAuthExpired(true);
          break;
        default:
          setLoadError(true);
      }
    });
    return () => {
      disposed = true;
      chatApiRef.current = null;
      src.disconnect().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymous, myAppId, channelId, liveIdMode ? props.liveId : props.parentMessageId, reloadTick]);

  // CORE-1685 (QA george 31.10.2026): the practice page's comments had no reactions at all — no
  // picker, no chips, not even a server-side reaction with its count — while the same comment in
  // the master's full chat showed it. The channel is the same `ChatApiChannel`, so the toggle goes
  // through its `react` (AddReaction / RemoveReaction, own-record override, CORE-1479), and the
  // chips redraw from the next `onChange`. A failed call is said in the same error line as a send.
  // CORE-1685 (ревью 02.11) — те же правила, что в полном чате мастера (`myReactionCalls`,
  // CORE-1479): новая реакция снимает прежние свои, снятие идёт тем слагом, что лежит на
  // сервере (старый `love` под чипом ладони). Вызовы — по одному, отказ — в строку ошибки.
  const handleReact = useCallback((messageId: string, type: string, add: boolean, mine: readonly string[]) => {
    const src = chatApiRef.current;
    if (!src) return;
    setSendError(null);
    void (async () => {
      try {
        for (const call of myReactionCalls(mine, type, add)) {
          await src.react(messageId, call.type, call.add);
        }
      } catch (e: unknown) {
        setSendError(friendlyApiError(e));
      }
    })();
  }, []);

  const handleSend = useCallback(async () => {
    setSendError(null);
    // Reply via the hub (SendMessage carries the parent). Seeding a brand-new
    // thread isn't supported over ChatApi (event cards are backend-created), so a
    // no-parent send is a no-op.
    const src = chatApiRef.current;
    const text = brandifyEmoji(draft.trim(), canUseReaction);
    if (!src || !text) return;
    // CORE-1685 (QA 19.10) — корня нет: раньше отправка молча ничего не делала,
    // текст оставался в поле, и человек не понимал, что произошло.
    if (!parentMessageId) {
      setSendError(NO_ROOT_SEND_ERROR);
      return;
    }
    try {
      await src.send(text, parentMessageId);
      setDraft('');
    } catch (e: any) {
      setSendError(friendlyApiError(e));
    }
  }, [draft, parentMessageId, canUseReaction]);

  const handleConfirmJoin = useCallback(async () => {
    if (!masterProfileId) return;
    try {
      // "Присоединиться к событию" = subscribe to master. Backend
      // MasterSubscriptionService.SubscribeAsync wires the circle channel
      // membership server-side (EventThreadService.AddCircleMemberAsync), so a
      // single call also unlocks the comment thread.
      await api.subscribeToMaster(masterProfileId);
      try { await refreshContacts(); } catch { /* non-fatal */ }
      setIsMember(true);
      setShowJoinModal(false);
      setReloadTick((n) => n + 1);
      setTimeout(() => inputRef.current?.focus(), 200);
    } catch (e: any) {
      setSendError(e?.message || 'Не удалось присоединиться');
    }
  }, [masterProfileId, refreshContacts]);

  const handleInputPress = useCallback(() => {
    if (anonymous) {
      navigation.navigate('Login');
      return;
    }
    if (!isMember) setShowJoinModal(true);
  }, [anonymous, isMember, navigation]);

  // CORE-949 — the discussion author gets the same level badge as the circle
  // feed (AgapellaChatThread) and comments (CommentsModal). Community-chat
  // authors are regular users, so — unlike the CORE-746 master-only surfaces —
  // this is an eligible gamification surface. One micro-batched, light_path-gated
  // lookup (CORE-269/273) covers the parent card and every reply on screen.
  const { badges: authorBadges, statuses: authorBadgeStatuses } = useLevelBadgesState([
    parent?.user?.id,
    ...replies.map((m) => m.user?.id),
  ]);

  const colors = isDark
    ? { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)', text: '#fff', muted: 'rgba(255,255,255,0.55)', placeholder: 'rgba(255,255,255,0.4)' }
    : { bg: 'rgba(255,255,255,0.6)', border: 'rgba(15,20,25,0.08)', text: '#0F1419', muted: 'rgba(15,20,25,0.6)', placeholder: 'rgba(15,20,25,0.5)' };

  // ── Render branches ──────────────────────────────────────────────────

  // CORE-543 — transient load failure (ChatApi timeout or network blip): a
  // retriable state, not the permanent "недоступно" dead-end. Checked first so a
  // flaky connection never masquerades as a deleted discussion.
  if (loadError) {
    return (
      <View style={styles.section}>
        <SectionHeader text="Обсуждение" colors={colors} />
        <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border }]}>
          <Text style={[styles.muted, { color: colors.muted }]}>
            Не удалось загрузить обсуждение. Проверьте соединение.
          </Text>
          <TouchableOpacity
            style={[styles.lockedInput, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border }]}
            onPress={() => { setLoadError(false); setReloadTick((n) => n + 1); }}
            activeOpacity={0.7}
          >
            <Ionicons name="refresh-outline" size={16} color={colors.muted} style={{ marginRight: 8 }} />
            <Text style={{ color: colors.muted, fontSize: 14 }}>Повторить</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // CORE-1127 (att3) — the token was rejected (HTTP 401 on negotiate). This is
  // an authorization failure, not a network blip, so it must NOT wear the
  // «Проверьте соединение.» copy — that was the whole bug. Offer a re-sign-in,
  // which mints a fresh token; the screen remounts and reconnects.
  if (authExpired) {
    return (
      <View style={styles.section}>
        <SectionHeader text="Обсуждение" colors={colors} />
        <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border }]}>
          <Text style={[styles.muted, { color: colors.muted }]}>
            Не удалось подтвердить вход. Войдите заново, чтобы открыть обсуждение.
          </Text>
          <TouchableOpacity
            style={[styles.lockedInput, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border }]}
            onPress={() => navigation.navigate('Login')}
            activeOpacity={0.7}
          >
            <Ionicons name="log-in-outline" size={16} color={colors.muted} style={{ marginRight: 8 }} />
            <Text style={{ color: colors.muted, fontSize: 14 }}>Войти</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (unavailable) {
    return (
      <View style={styles.section}>
        <SectionHeader text="Обсуждение" colors={colors} />
        <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border }]}>
          <Text style={[styles.muted, { color: colors.muted }]}>Обсуждение недоступно.</Text>
        </View>
      </View>
    );
  }

  // CORE-1685 — пока корень ищется по старым страницам, это загрузка, а не
  // «будьте первым» с композером, которому некуда отправлять.
  const searchingRoot = allowStart && !parentMessageId && rootSearch === 'searching';
  if ((loading || searchingRoot) && !anonymous) {
    return (
      <View style={styles.section}>
        <SectionHeader text="Обсуждение" colors={colors} />
        <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border, alignItems: 'center' }]}>
          <ActivityIndicator size="small" color={colors.muted} />
        </View>
      </View>
    );
  }

  // Anonymous: empty thread + login gate.
  if (anonymous) {
    return (
      <View style={styles.section}>
        <SectionHeader text="Обсуждение" colors={colors} />
        <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border }]}>
          <Text style={[styles.muted, { color: colors.muted, marginBottom: 12 }]}>
            Присоединяйтесь к обсуждению — войдите, чтобы читать и писать.
          </Text>
          <TouchableOpacity
            style={[styles.lockedInput, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border }]}
            onPress={handleInputPress}
            activeOpacity={0.7}
          >
            <Ionicons name="lock-closed-outline" size={16} color={colors.muted} style={{ marginRight: 8 }} />
            <Text style={{ color: colors.muted, fontSize: 14, flex: 1 }} numberOfLines={1}>
              Войди, чтобы присоединиться
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (!parentMessageId && !allowStart && !accessDenied) {
    // No event message in the channel yet, and this surface has its own
    // «be the first» CTA (AnnouncementChatPanel above) — rendering a second
    // wide empty-state box on top just creates a confusing dead strip.
    // CORE-1127 — a «forbidden» refusal still renders the shell so the
    // non-member sees the «Присоединиться» gate rather than nothing.
    return null;
  }
  // CORE-352 — with `allowStart` (practice page) we fall through and render the
  // discussion shell even without a parent card: members see the composer;
  // non-members see the join gate.

  const frozen = !!parent?.frozen;

  return (
    <View style={styles.section}>
      <SectionHeader text="Обсуждение" colors={colors} />
      <View style={[styles.box, { backgroundColor: colors.bg, borderColor: colors.border }]}>
        {parent && !parentIsTopic && (
          <MessageBubble
            msg={parent}
            colors={colors}
            isParent
            badge={parent.user?.id ? authorBadges[parent.user.id] ?? null : null}
            badgeStatus={(parent.user?.id ? authorBadgeStatuses[parent.user.id] : undefined) ?? 'disabled'}
            onReact={isMember && !frozen ? handleReact : undefined}
          />
        )}

        {replies.length === 0 ? (
          <Text style={[styles.muted, { color: colors.muted, marginTop: 12 }]}>
            Будьте первым, кто оставит комментарий.
          </Text>
        ) : (
          replies.map((m) => (
            <MessageBubble
              key={m.id}
              msg={m}
              colors={colors}
              badge={m.user?.id ? authorBadges[m.user.id] ?? null : null}
              badgeStatus={(m.user?.id ? authorBadgeStatuses[m.user.id] : undefined) ?? 'disabled'}
              onReact={isMember && !frozen ? handleReact : undefined}
            />
          ))
        )}

        {!frozen && (
          <>
            {isMember && emojiOpen && (
              <EmojiStrip onPick={pickEmoji} size={26} style={styles.emojiStrip} testID="event-thread-emoji-strip" />
            )}
          <View style={styles.inputRow}>
            {isMember && !parentMessageId ? (
              // CORE-1685 — обсуждения у практики нет (страницы кончились):
              // честная подсказка вместо композера, который ничего не отправит.
              <View
                style={[styles.lockedInput, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border }]}
                testID="event-thread-no-root"
              >
                <Ionicons name="information-circle-outline" size={16} color={colors.muted} style={{ marginRight: 8 }} />
                <Text style={{ color: colors.muted, fontSize: 14, flex: 1 }}>{NO_ROOT_HINT}</Text>
              </View>
            ) : isMember ? (
              <>
                <TextInput
                  ref={inputRef as any}
                  value={draft}
                  onChangeText={setDraft}
                  placeholder="Напишите комментарий…"
                  placeholderTextColor={colors.placeholder}
                  style={[
                    styles.input,
                    { color: colors.text, backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border },
                  ]}
                  multiline
                />
                <TouchableOpacity
                  onPress={() => setEmojiOpen((v) => !v)}
                  style={[styles.emojiBtn, { borderColor: colors.border }]}
                  accessibilityRole="button"
                  accessibilityLabel="Эмодзи"
                  accessibilityState={{ expanded: emojiOpen }}
                  testID="event-thread-emoji-button"
                >
                  <Ionicons name={emojiOpen ? 'close-outline' : 'happy-outline'} size={22} color={emojiOpen ? '#E98245' : colors.muted} />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleSend}
                  disabled={!draft.trim()}
                  style={[styles.sendBtn, !draft.trim() && { opacity: 0.4 }]}
                  accessibilityRole="button"
                  accessibilityLabel="Отправить комментарий"
                  testID="event-thread-send"
                >
                  <Ionicons name="send" size={18} color="#fff" />
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity
                style={[styles.lockedInput, { backgroundColor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.85)', borderColor: colors.border }]}
                onPress={handleInputPress}
                activeOpacity={0.7}
              >
                <Ionicons name="lock-closed-outline" size={16} color={colors.muted} style={{ marginRight: 8 }} />
                <Text style={{ color: colors.muted, fontSize: 14, flex: 1 }} numberOfLines={1}>
                  Чтобы написать, подпишись на мастера{masterName ? ` ${masterName}` : ''}
                </Text>
              </TouchableOpacity>
            )}
          </View>
          </>
        )}

        {sendError ? <Text style={styles.errorText}>{sendError}</Text> : null}
      </View>

      <JoinEventModal
        visible={showJoinModal}
        masterName={masterName}
        onCancel={() => setShowJoinModal(false)}
        onConfirm={handleConfirmJoin}
      />
    </View>
  );
};

/** CORE-409 — map an Agape.ChatApi message (services shape) to EventThread's
 *  local ThreadMessage. Event-card custom fields ride the parsed `raw.custom`
 *  bag; the parent id is carried on `quoted.id`. */
function chatApiToLocal(m: ChatApiThreadMessage): ThreadMessage {
  const custom = ((m.raw as any)?.custom || {}) as any;
  return {
    id: m.id,
    text: m.text ?? null,
    user: m.senderId ? { id: m.senderId, name: m.senderName ?? null, image: m.senderAvatar ?? null } : null,
    created_at: m.createdAt ?? null,
    type: custom.isEventCard || custom.eventId != null ? 'live_event' : undefined,
    frozen: false,
    eventKind: custom.eventKind,
    eventId: custom.eventId ?? custom.event_id,
    status: custom.status,
    deeplink: custom.deeplink,
    parent_id: m.quoted?.id ?? null,
    pinned: !!m.pinned,
    reactions: (m.reactions ?? []).map((r) => ({ type: r.type, count: r.count })),
    myReactions: m.myReactions ?? [],
  };
}

const SectionHeader: React.FC<{ text: string; colors: any }> = ({ text, colors }) => (
  <Typography variant="overline" style={[styles.sectionHeader, { color: colors.muted }]}>
    {text}
  </Typography>
);

const MessageBubble: React.FC<{
  msg: ThreadMessage;
  colors: any;
  isParent?: boolean;
  /** CORE-949 — the author's level badge, resolved by the parent thread. */
  badge?: LevelBadgeData | null;
  badgeStatus?: LevelBadgeLoadStatus;
  /** CORE-1685 — toggle a reaction of the kit; absent for a non-member or a frozen thread (chips stay read-only). */
  onReact?: (messageId: string, type: string, add: boolean, mine: readonly string[]) => void;
}> = ({ msg, colors, isParent, badge, badgeStatus = 'disabled', onReact }) => {
  const { guardAdd } = useRewardReactions();
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMine = (type: string) => ownReactionSlugs(msg.myReactions, type).length > 0;
  const toggle = (type: string) => {
    if (!onReact) return;
    const add = !isMine(type);
    if (add && !guardAdd(type)) return;
    onReact(msg.id, type, add, msg.myReactions);
  };
  const time = msg.created_at
    ? new Date(msg.created_at as any).toLocaleString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
      })
    : '';
  // CORE-2058 — правило 03.11 для всех чатов веба: 1–3 эмодзи комплекта без
  // текста — крупно и без рамки карточки; иначе строкой. Карточка события
  // (`isParent`) остаётся карточкой.
  const emojiOnly = isParent ? null : emojiOnlyTokens(msg.text, EMOJI_ONLY_MAX);
  return (
    <View style={[styles.bubble, isParent && styles.bubbleParent, { borderColor: colors.border }, !!emojiOnly && styles.bubbleEmojiOnly]}>
      <View style={styles.bubbleHeader}>
        <View style={styles.bubbleAuthorBlock}>
          <Text style={[styles.bubbleAuthor, { color: colors.text }]} numberOfLines={1}>
            {resolveName(msg.user?.name || msg.user?.id, 'Anonymous')}
            {isParent ? ' · событие' : ''}
          </Text>
          {/* CORE-949 — same level badge the circle feed and comments show. */}
          <LevelBadge badge={badge} status={badgeStatus} size="compact" style={styles.bubbleBadge} />
        </View>
        {!!time && <Text style={[styles.bubbleTime, { color: colors.muted }]}>{time}</Text>}
      </View>
      {msg.pinned ? (
        <View style={styles.pinnedInline}>
          <Ionicons name="pin" size={11} color="#E98245" style={{ transform: [{ rotate: '45deg' }] }} />
          <Text style={styles.pinnedInlineText}>Закреплено</Text>
        </View>
      ) : null}
      {/* CORE-1685 — токены `:agape_N:` в комментарии рисуются картинками. */}
      {emojiOnly ? (
        <EmojiOnlyMessage types={emojiOnly} testID="event-thread-emoji-only" />
      ) : !!msg.text && (
        <Text style={[styles.bubbleText, { color: colors.text }]}>
          <EmojiText text={msg.text} size={18} />
        </Text>
      )}
      {/* CORE-1685 (QA 31.10) — the comment's reactions: a chip per kit reaction with its count
          (1 included), the own one outlined; a tap toggles it. The smiley opens the kit's strip
          for a new reaction; a closed reward is guarded by `guardAdd` as everywhere else. */}
      {(msg.reactions.length > 0 || onReact) && (
        <View style={styles.reactionsRow} testID="event-thread-reactions">
          {groupByCanonicalReaction(msg.reactions).map(({ type, count }) => {
            const mine = isMine(type);
            return (
              <TouchableOpacity
                key={type}
                onPress={() => toggle(type)}
                disabled={!onReact}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityState={{ selected: mine }}
                testID={mine ? 'chat-reaction-mine' : 'chat-reaction'}
                style={[styles.reactionChip, { borderColor: mine ? '#E98245' : colors.border, backgroundColor: mine ? 'rgba(233, 130, 69,0.08)' : 'transparent' }]}
              >
                <ReactionGlyph type={type} size={18} />
                <Text style={[styles.reactionCount, { color: colors.muted }]} testID="chat-reaction-count">{count}</Text>
              </TouchableOpacity>
            );
          })}
          {onReact && (
            <TouchableOpacity
              onPress={() => setPickerOpen((v) => !v)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={pickerOpen ? 'Скрыть реакции' : 'Поставить реакцию'}
              accessibilityState={{ expanded: pickerOpen }}
              testID="event-thread-react"
              style={[styles.reactBtn, { borderColor: colors.border }]}
            >
              <Ionicons name={pickerOpen ? 'close-outline' : 'happy-outline'} size={16} color={colors.muted} />
            </TouchableOpacity>
          )}
        </View>
      )}
      {onReact && pickerOpen && (
        <EmojiStrip
          size={26}
          testID="event-thread-reaction-strip"
          style={styles.reactionStrip}
          onPick={(type) => { setPickerOpen(false); toggle(type); }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  section: { marginTop: 32, marginBottom: 24, paddingHorizontal: 16 },
  sectionHeader: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    letterSpacing: 2,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  box: { borderWidth: 1, borderRadius: 16, padding: 16, gap: 8 },
  muted: { fontSize: 14, lineHeight: 20 },
  bubble: { borderWidth: 1, borderRadius: 12, paddingVertical: 10, paddingHorizontal: 12, marginBottom: 8 },
  bubbleParent: { backgroundColor: 'rgba(233, 130, 69,0.08)' },
  bubbleEmojiOnly: { borderColor: 'transparent', backgroundColor: 'transparent' },
  bubbleHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 12 },
  bubbleAuthorBlock: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1, minWidth: 0 },
  bubbleAuthor: { fontSize: 13, fontWeight: fontWeights.bold, flexShrink: 1 },
  bubbleBadge: { flexShrink: 0 },
  bubbleTime: { fontSize: 11 },
  bubbleText: { fontSize: 14, lineHeight: 20 },
  pinnedInline: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 5 },
  reactionsRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 6 },
  reactionChip: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 2, paddingHorizontal: 8, borderRadius: 999, borderWidth: 1 },
  reactionCount: { fontSize: 12 },
  reactBtn: { width: 28, height: 24, borderRadius: 999, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  reactionStrip: { marginTop: 6 },
  pinnedInlineText: { color: '#E98245', fontSize: 11, fontWeight: fontWeights.bold },
  inputRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginTop: 12 },
  emojiStrip: { marginTop: 8 },
  emojiBtn: {
    width: 44, height: 44, borderRadius: 12, borderWidth: 1,
    alignItems: 'center', justifyContent: 'center',
  },
  input: {
    flex: 1,
    minHeight: 44,
    maxHeight: 120,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: 12,
    fontSize: 14,
    ...(Platform.OS === 'web' ? { outlineWidth: 0 as any } : {}),
  } as any,
  sendBtn: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: '#E98245', alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      backgroundImage: 'linear-gradient(135deg, #F45122, #FF955D)',
      boxShadow: '0 8px 22px rgba(237, 116, 61, 0.32)',
      cursor: 'pointer',
    } as any : { elevation: 7 }),
  },
  lockedInput: {
    flex: 1, flexDirection: 'row', alignItems: 'center',
    minHeight: 44, paddingHorizontal: 14, paddingVertical: 10,
    borderWidth: 1, borderRadius: 12,
  },
  errorText: { color: '#E53E3E', fontSize: 13, marginTop: 6 },
});

export default EventThread;
